// Ситуация цикла: командный хаб, домашний экран мира, NPC-фид,
// игровые комнаты, showcase-модалка, персистентность рабочего стола.
// Вынесено из CycleTab.tsx без изменения поведения (Фаза 1).

import { useState, useEffect, useMemo } from "react";
import {
  type CampaignSetup,
  type CommandHub,
  type CommandHubResource,
  type CommandHubRisk,
  type GameCycle,
} from "../api";
import { DossierModalView, type DossierSection } from "../DossierModalView";
import { ChronicleSyntheticDetails } from "./ChronicleSection";
import {
  CycleFlowGuide,
  CycleImages,
  GAME_UI_ASSET_BASE,
  OwnerForcePhaseControl,
  Paragraphs,
  SectionTitle,
  buildChronicleShowcaseItems,
  cycleNextStep,
  npcVisitDeadlinePassed,
  phaseInfo,
  previewText,
  type ActionWorkspaceTab,
  type ChronicleFeedItem,
  type ChronicleShowcaseItem,
  type Props,
  type Section,
} from "./shared";

const ROOM_ICON_SRC: Record<string, string> = {
  actions: `${GAME_UI_ASSET_BASE}/icon_room_actions.png`,
  council: `${GAME_UI_ASSET_BASE}/icon_room_council.png`,
  chat: `${GAME_UI_ASSET_BASE}/icon_room_chats.png`,
  npc: `${GAME_UI_ASSET_BASE}/icon_room_npc.png`,
  chronicle: `${GAME_UI_ASSET_BASE}/icon_room_chronicle.png`,
  world: `${GAME_UI_ASSET_BASE}/icon_room_world.png`,
  dossier: `${GAME_UI_ASSET_BASE}/icon_dossier.png`,
  members: `${GAME_UI_ASSET_BASE}/icon_room_council.png`,
};
function SituationSection({
  campaignId,
  setup,
  action,
  onNavigate,
  onOpenTab,
}: Props & { onNavigate: (section: Section) => void }) {
  const cycle = setup.cycle.current!;
  if (setup.commandHub) {
    return (
      <WorldHomeScreen
        campaignId={campaignId}
        setup={setup}
        hub={setup.commandHub}
        cycle={cycle}
        action={action}
        onNavigate={onNavigate}
        onOpenTab={onOpenTab}
      />
    );
  }
  const unreadChats = cycle.rooms.reduce(
    (sum, room) => sum + room.unreadCount,
    0,
  );
  const activeNpc = cycle.npcVisits.filter(
    (visit) =>
      visit.status === "ACTIVE" &&
      (visit.isInvited || visit.roomType === "NPC_PUBLIC") &&
      !npcVisitDeadlinePassed(visit),
  ).length;
  const pendingInitiatives = cycle.initiatives.filter(
    (item) => item.status !== "MERGED" && item.consolidatedIntoId == null,
  ).length;
  const threatCount = setup.threats.tracks.length;
  const situationPreview = previewText(cycle.situationSummary, 420);
  return (
    <div className="cc-cycle-section-stack cc-game-hub-stack">
      <section className="cc-block cc-game-episode-card cc-situation-card">
        <CycleImages
            campaignId={campaignId}
            assets={cycle.openingImages}
            label="Иллюстрации ситуации"
            canManage={setup.canManage}
            action={action}
        />
        <div className="cc-episode-overlay">
          <SectionTitle
            icon="⚠"
            eyebrow={`Цикл ${cycle.cycleNumber} · ${phaseInfo(cycle.phase).label}`}
            title={cycle.centralProblem ?? cycle.title}
          />
          <p className="cc-situation-preview-text">{situationPreview}</p>
          <button
            type="button"
            className="cc-text-link cc-situation-chronicle-link"
            onClick={() => onNavigate("chronicle")}
          >
            Читать подробности в летописи
          </button>
          <div className="cc-episode-metrics">
            <span>
              <b>
                {cycle.actionsUsed}/{cycle.actionLimit}
              </b>{" "}
              ваши действия
            </span>
            <span>
              <b>{cycle.initiatives.length}</b> инициатив
            </span>
            <span>
              <b>{threatCount}</b> угроз
            </span>
          </div>
          {cycle.complications.length > 0 && (
            <div className="cc-complication-grid cc-episode-complications">
              {cycle.complications.slice(0, 3).map((item, index) => (
                <article key={`${index}-${item}`}>
                  <b>{index + 1}</b>
                  <p>{item}</p>
                </article>
              ))}
            </div>
          )}
        </div>
        
      </section>


      <section className="cc-block cc-game-room-hub">
        <SectionTitle
          icon="⌂"
          eyebrow="Штаб кампании"
          title="Игровые комнаты"
        />
        <div className="cc-game-room-grid">
          <GameRoomTile
            tone="actions"
            title="Действия"
            subtitle={`${cycle.actions.length} ходов · осталось ${Math.max(0, cycle.actionLimit - cycle.actionsUsed)}`}
            badge={cycle.canAct ? "!" : undefined}
            onClick={() => onNavigate("actions")}
          />
          <GameRoomTile
            tone="chat"
            title="Чаты"
            subtitle={
              activeNpc
                ? `${cycle.rooms.length} комнат · ${activeNpc} NPC ждёт ответа`
                : `${cycle.rooms.length} комнат · ${unreadChats} новых`
            }
            badge={activeNpc ? `${activeNpc}` : unreadChats ? `${unreadChats}` : undefined}
            onClick={() => onNavigate("actions")}
          />
          <GameRoomTile
            tone="council"
            title="Совет"
            subtitle={`${setup.members.length} участник${setup.members.length === 1 ? "" : setup.members.length < 5 ? "а" : "ов"} · ${pendingInitiatives} решений`}
            badge={
              cycle.unread.initiativeCount
                ? `${cycle.unread.initiativeCount}`
                : undefined
            }
            onClick={() => onNavigate("actions")}
          />
          <GameRoomTile
            tone="chronicle"
            title="Летопись"
            subtitle={`${cycle.chronicleEvents.length} событий цикла`}
            badge={
              cycle.unread.chronicleCount
                ? `${cycle.unread.chronicleCount}`
                : undefined
            }
            onClick={() => onNavigate("chronicle")}
          />
          <GameRoomTile
            tone="world"
            title="Мир"
            subtitle={`${threatCount} угроз · ресурсы и фракции`}
            onClick={() => onOpenTab?.("world")}
          />
          {setup.myPrivateProfile && (
            <GameRoomTile
              tone="dossier"
              title="Досье"
              subtitle="роль, цель и скрытые сведения"
              onClick={() => onOpenTab?.("dossier")}
            />
          )}
        </div>
      </section>

      <CycleFlowGuide activePhase={cycle.phase} />
    </div>
  );
}
function WorldHomeScreen({
  campaignId,
  setup,
  hub,
  cycle,
  action,
  onNavigate,
  onOpenTab,
}: {
  campaignId: string;
  setup: CampaignSetup;
  action: Props["action"];
  hub: CommandHub;
  cycle: GameCycle;
  onNavigate: (section: Section) => void;
  onOpenTab?: (tab: "chronicle" | "world" | "council" | "dossier") => void;
}) {
  const [openSituation, setOpenSituation] = useState(false);
  const [openingItem, setOpeningItem] = useState<ChronicleShowcaseItem | null>(null);
  const showcaseItems = useMemo(
    () => buildChronicleShowcaseItems(setup, cycle),
    [setup.world, setup.cycle.chronicleChapters, cycle.id, cycle.situationSummary, cycle.closingNarrative, cycle.openingImages, cycle.chronicleComicRecapImages, cycle.worldChanges],
  );
  const latestShowcase = showcaseItems[showcaseItems.length - 1] ?? null;

  useEffect(() => {
    const target = latestShowcase;
    if (!target) return;
    const storageKey = chronicleIntroSeenKey(campaignId);
    if (readPersistentValue(storageKey) === target.seenKey) return;
    writePersistentValue(storageKey, target.seenKey);
    setOpeningItem(target);
  }, [campaignId, latestShowcase]);

  const openActionsTab = (tab: ActionWorkspaceTab) => {
    rememberPreferredActionWorkspace(campaignId, cycle.id, tab);
    onNavigate("actions");
  };

  return (
    <div className="cc-world-home-shell" aria-label="Мир кампании">
      <section className="cc-world-home-topbar">
        <div>
          <small>Мир</small>
          <h2>{hub.header.worldName ?? setup.world?.worldName ?? hub.header.campaignTitle}</h2>
        </div>
        <span>Цикл {hub.header.cycleNumber} · {hub.header.phaseLabel}</span>
      </section>

      <button type="button" className="cc-world-panel cc-world-situation-card" onClick={() => setOpenSituation(true)}>
        <div className="cc-world-panel-title">
          <img src={`${GAME_UI_ASSET_BASE}/icon_chronicle_situation.png`} alt="" />
          <h3>Текущая ситуация</h3>
        </div>
        <strong>{hub.situation.title}</strong>
        <p>{hub.situation.shortText}</p>
        <b>{hub.situation.mainObjective}</b>
      </button>

      {hub.resources.length > 0 && (
        <section className="cc-world-panel cc-world-resources-panel">
          <div className="cc-world-panel-title"><img src={`${GAME_UI_ASSET_BASE}/icon_resource.png`} alt="" /><h3>Ресурсы города</h3></div>
          <div className="cc-command-resource-row cc-world-resource-row">
            {hub.resources.map((resource) => <CommandResourceChip key={resource.code} resource={resource} />)}
          </div>
        </section>
      )}

      {hub.risks.length > 0 && (
        <section className="cc-world-panel cc-world-risks-panel">
          <div className="cc-world-panel-title"><img src={`${GAME_UI_ASSET_BASE}/icon_threat.png`} alt="" /><h3>Главные риски</h3></div>
          <div className="cc-command-risk-list">
            {hub.risks.map((risk) => <CommandRiskRow key={risk.code} risk={risk} />)}
          </div>
        </section>
      )}

      <section className="cc-world-panel cc-world-next-panel">
        <div className="cc-world-panel-title"><img src={`${GAME_UI_ASSET_BASE}/icon_vote.png`} alt="" /><h3>Что дальше</h3></div>
        <p>{cycleNextStep(cycle)}</p>
        <div className="cc-world-next-actions">
          {cycle.phase === "DAY" && (
            <>
              <button type="button" className="primary" onClick={() => openActionsTab("actions")}>Выполнить действие</button>
              <button type="button" onClick={() => openActionsTab("chats")}>Чаты и встречи</button>
            </>
          )}
          {cycle.phase === "AGENDA" && (
            <>
              {cycle.canEditInitiative && (
                <button type="button" className="primary" onClick={() => openActionsTab("initiatives")}>Создать инициативу</button>
              )}
              <button type="button" onClick={() => openActionsTab("initiatives")}>Перейти к инициативам</button>
            </>
          )}
          {cycle.phase === "VOTING" && (
            <>
              <button type="button" className="primary" onClick={() => openActionsTab("initiatives")}>Голосовать</button>
              <button type="button" onClick={() => onOpenTab?.("chronicle")}>Открыть хронику</button>
            </>
          )}
          {cycle.phase !== "DAY" && cycle.phase !== "AGENDA" && cycle.phase !== "VOTING" && (
            <button type="button" className="primary" onClick={() => openActionsTab(defaultActionWorkspace(cycle))}>Открыть раздел действий</button>
          )}
        </div>
      </section>

      {openSituation && (
        <CommandHubModal
          kind="situation"
          hub={hub}
          campaignId={campaignId}
          setup={setup}
          cycle={cycle}
          action={action}
          onClose={() => setOpenSituation(false)}
        />
      )}
      {openingItem && (
        <ChronicleShowcaseModal
          campaignId={campaignId}
          item={openingItem}
          canManage={setup.canManage}
          action={action}
          onClose={() => setOpeningItem(null)}
        />
      )}
      <OwnerForcePhaseControl campaignId={campaignId} setup={setup} action={action} />
    </div>
  );
}
type CommandHubModalKind = "dossier" | "intel" | "situation";
function CommandHubModal({
  kind,
  hub,
  campaignId,
  setup,
  cycle,
  action,
  onClose,
}: {
  kind: CommandHubModalKind;
  hub: CommandHub;
  campaignId: string;
  setup: CampaignSetup;
  cycle: GameCycle;
  action: Props["action"];
  onClose: () => void;
}) {
  if (kind === "situation") {
    const situationItem = commandHubSituationFeedItem(hub, cycle, setup);
    return (
      <div className="cc-modal-backdrop cc-chronicle-modal-backdrop cc-command-modal-backdrop" role="dialog" aria-modal="true">
        <div className="cc-modal-card cc-chronicle-modal-card cc-command-situation-chronicle-modal">
          <header className="cc-modal-header cc-chronicle-modal-header-minimal">
            <button type="button" className="cc-modal-close" onClick={onClose}>
              ×
            </button>
          </header>
          <div className="cc-chronicle-modal-body">
            <ChronicleSyntheticDetails
              campaignId={campaignId}
              item={situationItem}
              canManage={setup.canManage}
              action={action}
            />
          </div>
        </div>
      </div>
    );
  }

  const title = kind === "dossier" ? "Досье" : "Сведения";
  const dossierSection: DossierSection = kind === "intel" ? "briefing" : "intro";
  return (
    <div className="cc-modal-backdrop cc-command-modal-backdrop" role="dialog" aria-modal="true">
      <div className="cc-modal-card cc-command-modal cc-command-dossier-modal">
        <header className="cc-modal-header">
          <div>
            <span>Штаб</span>
            <h2>{title}</h2>
          </div>
          <button type="button" className="cc-modal-close" onClick={onClose}>×</button>
        </header>
        <div className="cc-command-dossier-embed">
          <DossierModalView
            campaignId={campaignId}
            setup={setup}
            section={dossierSection}
            action={action}
          />
        </div>
      </div>
    </div>
  );
}
function commandHubSituationFeedItem(
  hub: CommandHub,
  cycle: GameCycle,
  setup: CampaignSetup,
): Extract<ChronicleFeedItem, { kind: "prologue" | "cycle-situation" | "cycle-outcome" }> {
  const fullText = hub.situation.fullText || cycle.situationSummary || hub.situation.shortText;
  const body = [hub.situation.centralProblem || cycle.centralProblem, fullText]
    .filter(Boolean)
    .join("\n\n");
  return {
    kind: "cycle-situation",
    id: `command-hub-cycle-situation-${cycle.id}`,
    createdAt: cycle.openedAt || setup.world?.createdAt || new Date(0).toISOString(),
    title: hub.situation.title || cycle.title,
    summary: hub.situation.shortText || fullText || hub.situation.title,
    body: body || hub.situation.shortText || hub.situation.title,
    images: cycle.openingImages,
  };
}
function CommandResourceChip({ resource }: { resource: CommandHubResource }) {
  return (
    <button type="button" className={`cc-command-resource-chip ${resource.state.toLowerCase()}`} title={resource.displayName}>
      <img src={resourceIconAsset(resource.code)} alt="" />
      <b>{resource.label}</b>
      <strong>{resource.currentValue}/{resource.maxValue}</strong>
    </button>
  );
}
function CommandRiskRow({ risk }: { risk: CommandHubRisk }) {
  return (
    <article className={`cc-command-risk-row ${risk.level.toLowerCase()}`}>
      <img src={riskIconAsset(risk)} alt="" />
      <p>{risk.text}</p>
    </article>
  );
}
function resourceIconAsset(code: string): string {
  const normalized = code.toUpperCase();
  const file = normalized.includes("ENERGY")
    ? "icon_world_resource_energy.png"
    : normalized.includes("WATER")
      ? "icon_world_resource_water.png"
      : normalized.includes("SHELTER") || normalized.includes("HOUSING")
        ? "icon_world_resource_housing.png"
        : normalized.includes("MEDICAL") || normalized.includes("MEDICINE")
          ? "icon_world_resource_medicine.png"
          : normalized.includes("INFORMATION")
            ? "icon_world_resource_information.png"
            : normalized.includes("FOOD")
              ? "icon_world_resource_food.png"
              : "icon_resource.png";
  return `${GAME_UI_ASSET_BASE}/${file}`;
}
function riskIconAsset(risk: CommandHubRisk): string {
  const code = `${risk.code} ${risk.linkedCode}`.toUpperCase();
  const file = code.includes("INFRASTRUCTURE")
    ? "icon_world_indicator_development.png"
    : code.includes("DEFENSE") || code.includes("SECURITY") || code.includes("ORDER")
      ? "icon_world_indicator_security.png"
      : code.includes("ENERGY")
        ? "icon_world_resource_energy.png"
        : code.includes("WATER")
          ? "icon_world_resource_water.png"
          : "icon_threat.png";
  return `${GAME_UI_ASSET_BASE}/${file}`;
}
function GameRoomTile({
  tone,
  title,
  subtitle,
  badge,
  onClick,
}: {
  tone: string;
  title: string;
  subtitle: string;
  badge?: string;
  onClick?: () => void;
}) {
  const iconSrc = ROOM_ICON_SRC[tone];
  return (
    <button
      type="button"
      className={`cc-game-room-tile ${tone}`}
      onClick={onClick}
    >
      <span className="cc-game-room-icon">
        {iconSrc ? <img src={iconSrc} alt="" /> : "◆"}
      </span>
      <span className="cc-game-room-copy">
        <strong>{title}</strong>
        <small>{subtitle}</small>
      </span>
      {badge && <i>{badge}</i>}
    </button>
  );
}
function chronicleIntroSeenKey(campaignId: string): string {
  return `cc:${campaignId}:chronicle-intro-seen`;
}
function readPersistentValue(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writePersistentValue(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* optional */
  }
}
function defaultActionWorkspace(cycle: GameCycle): ActionWorkspaceTab {
  if (cycle.phase === "AGENDA" || cycle.phase === "VOTING") return "initiatives";
  return "actions";
}
function preferredActionWorkspaceKey(campaignId: string, cycleId: string): string {
  return `cc:${campaignId}:${cycleId}:preferred-action-workspace`;
}
function rememberPreferredActionWorkspace(campaignId: string, cycleId: string, tab: ActionWorkspaceTab) {
  try {
    window.sessionStorage.setItem(preferredActionWorkspaceKey(campaignId, cycleId), tab);
  } catch {
    // Browser storage can be unavailable in embedded WebViews. Navigation still works.
  }
}
function consumePreferredActionWorkspace(campaignId: string, cycleId: string): ActionWorkspaceTab | null {
  try {
    const key = preferredActionWorkspaceKey(campaignId, cycleId);
    const value = window.sessionStorage.getItem(key);
    if (value) window.sessionStorage.removeItem(key);
    return value === "actions" || value === "chats" || value === "initiatives" ? value : null;
  } catch {
    return null;
  }
}
function ChronicleShowcaseModal({
  campaignId,
  item,
  canManage,
  action,
  onClose,
}: {
  campaignId: string;
  item: ChronicleShowcaseItem;
  canManage: boolean;
  action: Props["action"];
  onClose: () => void;
}) {
  return (
    <div className="cc-modal-backdrop cc-chronicle-modal-backdrop" role="dialog" aria-modal="true">
      <div className="cc-modal-card cc-chronicle-modal-card cc-chronicle-showcase-modal">
        <header className="cc-modal-header cc-chronicle-modal-header-minimal">
          <button type="button" className="cc-modal-close" onClick={onClose}>
            ×
          </button>
        </header>
        <div className="cc-chronicle-modal-body">
          <article className="cc-chronicle-detail-entry cc-chronicle-synthetic-detail">
            <div className="cc-chronicle-detail-meta">
              <span>{item.meta}</span>
            </div>
            <h2 className="cc-chronicle-modal-title">{item.title}</h2>
            {item.images.length > 0 ? (
              <CycleImages
                campaignId={campaignId}
                assets={item.images}
                label={item.title}
                canManage={canManage}
                action={action}
              />
            ) : (
              <div className="cc-world-comic-placeholder">
                <img src={`${GAME_UI_ASSET_BASE}/icon_room_chronicle.png`} alt="" />
                <strong>Комикс ещё формируется</strong>
                <span>Как только генерация завершится, страницы главы появятся здесь.</span>
              </div>
            )}
            <section>
              <h3>{item.situationHeading}</h3>
              <Paragraphs text={item.situationText} />
            </section>
            <section>
              <h3>{item.outcomeHeading}</h3>
              <Paragraphs text={item.outcomeText} />
            </section>
          </article>
        </div>
      </div>
    </div>
  );
}

export {
  ROOM_ICON_SRC,
  SituationSection,
  WorldHomeScreen,
  CommandHubModal,
  commandHubSituationFeedItem,
  CommandResourceChip,
  CommandRiskRow,
  resourceIconAsset,
  riskIconAsset,
  GameRoomTile,
  ChronicleShowcaseModal,
  chronicleIntroSeenKey,
  readPersistentValue,
  writePersistentValue,
  defaultActionWorkspace,
  preferredActionWorkspaceKey,
  rememberPreferredActionWorkspace,
  consumePreferredActionWorkspace,
};
export type { CommandHubModalKind };
